import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Alert, AlertDescription } from './ui/alert';
import { Card } from './ui/card';
import { CheckCircle, AlertTriangle, ExternalLink, Lightbulb } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { getTenantUrl } from '../utils/tenantDetection';

interface SubdomainConfigurationProps {
  companyId: string;
  company: {
    subdomain?: string;
    subdomainEnabled?: boolean;
  };
  accessToken: string;
  onUpdate: () => void;
}

export function SubdomainConfiguration({
  companyId,
  company,
  accessToken,
  onUpdate
}: SubdomainConfigurationProps) {
  const [subdomain, setSubdomain] = useState(company.subdomain || '');
  const [enabled, setEnabled] = useState(company.subdomainEnabled ?? false);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!subdomain || subdomain === company.subdomain) {
      setAvailable(null);
      setError(null);
      return;
    }

    const checkAvailability = async () => {
      setChecking(true);
      setError(null);

      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/subdomain-availability/${subdomain}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          }
        );
        const data = await response.json();

        if (data.available) {
          setAvailable(true);
        } else {
          setAvailable(false);
          setError(data.error);
        }
      } catch (err) {
        setError('Failed to check availability');
      } finally {
        setChecking(false);
      }
    };

    const debounce = setTimeout(checkAvailability, 500);
    return () => clearTimeout(debounce);
  }, [subdomain, companyId, accessToken, company.subdomain]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${companyId}/subdomain`,
        {
          method: 'PATCH',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          body: JSON.stringify({ subdomain, enabled })
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update subdomain');
      }

      onUpdate();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const subdomainUrl = subdomain ? getTenantUrl(subdomain) : '';

  return (
    <Card className="p-6">
      <h3 className="text-xl mb-4">Custom Subdomain</h3>
      
      <Alert className="mb-6">
        <Lightbulb className="w-4 h-4" />
        <AlertDescription>
          Give your team a branded login experience with a custom subdomain. 
          Your users will access PathKit at <strong>{subdomainUrl || 'your-company.pathkit.pro'}</strong>
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {/* Subdomain Input */}
        <div>
          <Label htmlFor="subdomain">Subdomain</Label>
          <div className="flex items-center gap-2 mt-2">
            <Input
              id="subdomain"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase().trim())}
              placeholder="mycompany"
              required
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              title="Lowercase letters, numbers, and hyphens only"
            />
            <span className="text-gray-600">.pathkit.pro</span>
          </div>

          {checking && (
            <p className="text-sm text-gray-600 mt-2">Checking availability...</p>
          )}

          {available === true && (
            <div className="flex items-center gap-2 text-sm text-green-600 mt-2">
              <CheckCircle className="w-4 h-4" />
              <span>Available</span>
            </div>
          )}

          {available === false && error && (
            <div className="flex items-center gap-2 text-sm text-red-600 mt-2">
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <Label htmlFor="enabled">Enable Subdomain Access</Label>
            <p className="text-sm text-gray-600">
              Allow users to login via your custom subdomain
            </p>
          </div>
          <Switch
            id="enabled"
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </div>

        {/* Preview URL */}
        {subdomain && enabled && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-sm text-blue-900 mb-2">Your PathKit URL:</div>
            <a
              href={subdomainUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 flex items-center gap-2 break-all"
            >
              {subdomainUrl}
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
            </a>
          </div>
        )}

        {/* Save Button */}
        <div className="flex gap-3 pt-4">
          <Button
            onClick={handleSave}
            disabled={saving || checking || (available === false)}
          >
            {saving ? 'Saving...' : 'Save Subdomain Configuration'}
          </Button>
        </div>

        {/* Error Message */}
        {error && !available && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </Card>
  );
}